import { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
} from "@tanstack/react-table";
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { useQueryOptions } from "../filters/filters-context";
import { useConversations } from "../hooks/use-conversation-data";
import { useConversationTableSelection } from "../hooks/use-conversation-table-selection";
import { getConversationsTableColumns } from "./columns/conversations-columns";
import { DEFAULT_PAGE_SIZE } from "./types";

interface ConversationsTableContextValue {
  data: ConversationListItemResponse[];
  columns: ColumnDef<ConversationListItemResponse, unknown>[];
  isLoading: boolean;
  // Pagination
  currentPage: number;
  totalCount: number;
  pageSize: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  // Navigation & selection
  focusedConversationId: ConversationId | null;
  focusedConversation: ConversationListItemResponse | null;
  setFocusedConversationId: (id: ConversationId | null) => void;
  selectedConversationId: ConversationId | null;
  openConversation: (id?: ConversationId | null) => void;
  navigateUp: () => void;
  navigateDown: () => void;
  actionMenuOpenForId: ConversationId | null;
  setActionMenuOpenForId: (id: ConversationId | null) => void;
  columnOrder: ColumnOrderState;
  setColumnOrder: (order: ColumnOrderState) => void;
  columnSizing: ColumnSizingState;
  setColumnSizing: (sizing: ColumnSizingState) => void;
  selectedConversationIds: ConversationId[];
  selectedConversations: ConversationListItemResponse[];
  selectedConversationCount: number;
  hoveredConversationId: ConversationId | null;
  hasAnySelectedConversations: boolean;
  setHoveredConversationId: (id: ConversationId | null) => void;
  isConversationSelected: (id: ConversationId) => boolean;
  setConversationSelected: (input: {
    conversationId: ConversationId;
    rowIndex: number;
    checked: boolean;
    shiftKey: boolean;
  }) => void;
  setAllConversationsSelected: (checked: boolean) => void;
  clearSelectedConversations: () => void;
}

const ConversationsTableContext =
  createContext<ConversationsTableContextValue | null>(null);

export function useConversationsTableContext(): ConversationsTableContextValue {
  const context = useContext(ConversationsTableContext);
  if (!context) {
    throw new Error(
      "useConversationsTableContext must be used within a ConversationsTableProvider",
    );
  }
  return context;
}

export function useConversationsTableContextMaybe(): ConversationsTableContextValue | null {
  return useContext(ConversationsTableContext);
}

export const ConversationsTableProvider = memo(
  function ConversationsTableProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { teamId, conversationId: conversationIdParam } = useParams<{
      teamId: string;
      conversationId: string;
    }>();

    // Reset page to 0 when filters change (React pattern for adjusting state based on props)
    const queryOptions = useQueryOptions();
    const [currentPage, setCurrentPage] = useState(0);
    const [prevQueryOptions, setPrevQueryOptions] = useState(queryOptions);
    if (queryOptions !== prevQueryOptions) {
      setPrevQueryOptions(queryOptions);
      if (currentPage !== 0) {
        setCurrentPage(0);
      }
    }

    // Data is already filtered via useQueryOptions() inside useConversations()
    const { data, totalCount, isLoading } = useConversations(currentPage);

    // Pagination helpers
    const pageSize = DEFAULT_PAGE_SIZE;
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + data.length, totalCount);
    const hasPreviousPage = currentPage > 0;
    const hasNextPage = endIndex < totalCount;

    const goToNextPage = useCallback(() => {
      if (hasNextPage) {
        setCurrentPage((p) => p + 1);
      }
    }, [hasNextPage]);

    const goToPreviousPage = useCallback(() => {
      if (hasPreviousPage) {
        setCurrentPage((p) => p - 1);
      }
    }, [hasPreviousPage]);

    // On the detail view, selectedConversationId comes from the route param
    const selectedConversationId = conversationIdParam
      ? ConversationId.make(conversationIdParam)
      : null;

    // focusedConversationId: on table view, read from ?focused= query param;
    // on detail view, use the :conversationId route param
    const focusedConversationId = useMemo<ConversationId | null>(() => {
      if (selectedConversationId) {
        return selectedConversationId;
      }
      const searchParams = new URLSearchParams(location.search);
      const focused = searchParams.get("focused");
      return focused ? ConversationId.make(focused) : null;
    }, [selectedConversationId, location.search]);

    const setFocusedConversationId = useCallback(
      (id: ConversationId | null) => {
        // Only update URL on table view (no conversationId route param)
        if (selectedConversationId) {
          return;
        }
        const searchParams = new URLSearchParams(location.search);
        if (id) {
          searchParams.set("focused", id);
        } else {
          searchParams.delete("focused");
        }
        const newSearch = searchParams.toString();
        navigate(
          { search: newSearch ? `?${newSearch}` : "" },
          { replace: true },
        );
      },
      [selectedConversationId, location.search, navigate],
    );

    const openConversation = useCallback(
      (id?: ConversationId | null) => {
        const targetId = id ?? focusedConversationId;
        if (targetId && teamId) {
          navigate(
            `/stores/${teamId}/support-v2/${targetId}${location.search}`,
          );
        }
      },
      [focusedConversationId, teamId, navigate, location.search],
    );

    const [actionMenuOpenForId, setActionMenuOpenForId] =
      useState<ConversationId | null>(null);

    const [columnOrder, setColumnOrder] = useLocalStorage<ColumnOrderState>(
      "redo.support-v2.conversations-table.column-order",
      [],
    );
    const [columnSizing, setColumnSizing] = useLocalStorage<ColumnSizingState>(
      "redo.support-v2.conversations-table.column-sizing",
      {},
    );

    const columns = useMemo(
      () => getConversationsTableColumns({ enablePinning: true }),
      [],
    );

    const {
      selectedConversationIds,
      selectedConversations,
      selectedConversationCount,
      hoveredConversationId,
      hasAnySelectedConversations,
      setHoveredConversationId,
      isConversationSelected,
      setConversationSelected,
      setAllConversationsSelected,
      clearSelectedConversations,
    } = useConversationTableSelection(data);

    const focusedConversation = useMemo(() => {
      if (!focusedConversationId) {
        return null;
      }
      return data.find((c) => c.id === focusedConversationId) ?? null;
    }, [data, focusedConversationId]);

    const navigateUp = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedConversationId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setFocusedConversationId(data[data.length - 1].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedConversationId,
      );
      if (currentIndex <= 0) {
        return;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFocusedConversationId(data[currentIndex - 1].id);
    }, [data, focusedConversationId, setFocusedConversationId]);

    const navigateDown = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedConversationId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setFocusedConversationId(data[0].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedConversationId,
      );
      if (currentIndex === -1 || currentIndex >= data.length - 1) {
        return;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFocusedConversationId(data[currentIndex + 1].id);
    }, [data, focusedConversationId, setFocusedConversationId]);

    const value = useMemo<ConversationsTableContextValue>(
      () => ({
        data,
        columns,
        isLoading,
        // Pagination
        currentPage,
        totalCount,
        pageSize,
        hasPreviousPage,
        hasNextPage,
        goToNextPage,
        goToPreviousPage,
        // Navigation & selection
        focusedConversationId,
        focusedConversation,
        setFocusedConversationId,
        selectedConversationId,
        openConversation,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
        setActionMenuOpenForId,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        selectedConversationIds,
        selectedConversations,
        selectedConversationCount,
        hoveredConversationId,
        hasAnySelectedConversations,
        setHoveredConversationId,
        isConversationSelected,
        setConversationSelected,
        setAllConversationsSelected,
        clearSelectedConversations,
      }),
      [
        data,
        columns,
        isLoading,
        currentPage,
        totalCount,
        pageSize,
        hasPreviousPage,
        hasNextPage,
        goToNextPage,
        goToPreviousPage,
        focusedConversationId,
        focusedConversation,
        setFocusedConversationId,
        selectedConversationId,
        openConversation,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        selectedConversationIds,
        selectedConversations,
        selectedConversationCount,
        hoveredConversationId,
        hasAnySelectedConversations,
        setHoveredConversationId,
        isConversationSelected,
        setConversationSelected,
        setAllConversationsSelected,
        clearSelectedConversations,
      ],
    );

    return (
      <ConversationsTableContext.Provider value={value}>
        {children}
      </ConversationsTableContext.Provider>
    );
  },
);
